import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "./ui/accordion";

const EASE = [0.22, 1, 0.36, 1];

const FAQS = [
  {
    q: "Is DMWrapped safe? Do you see my messages?",
    a: "Yes — it's safe by design. Your Instagram export is analyzed entirely on your own phone, inside the app. Your messages are never uploaded, stored, or seen by us or anyone else. It even works fully offline.",
  },
  {
    q: "Does Instagram allow this?",
    a: "Yes. You download your own data using Instagram's official \"Download your information\" feature — it's your data and your right to have it. DMWrapped never asks for your Instagram password and never connects to your account.",
  },
  {
    q: "How do I get my Instagram data?",
    a: "In Instagram go to Settings → Accounts Center → Your information and permissions → Download your information. Choose Messages, JSON format, All time. Meta emails you a .zip within 24–72 hours — then just import it in the app.",
  },
  {
    q: "Are encrypted chats included?",
    a: "No. End-to-end encrypted chats aren't part of Instagram's data export, so they can't appear in your report.",
  },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export function Faq() {
  return (
    <section id="faq" data-testid="faq-section" className="mx-auto max-w-3xl scroll-mt-24 px-4 pb-24 sm:px-6 sm:pb-32">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      </Helmet>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, ease: EASE }}
        className="text-center"
      >
        <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-acid">
          Questions, answered
        </p>
        <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-paper sm:text-4xl">
          Everything you're wondering
        </h2>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
        className="mt-10"
      >
        <Accordion type="single" collapsible className="w-full">
          {FAQS.map((f, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              data-testid={`faq-item-${i}`}
              className="rounded-xl border border-white/[0.08] bg-panel px-5 mb-3 data-[state=open]:border-acid/40 transition-colors"
            >
              <AccordionTrigger
                data-testid={`faq-question-${i}`}
                className="py-5 text-left font-display text-base font-bold text-paper hover:no-underline [&>svg]:text-acid"
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent
                data-testid={`faq-answer-${i}`}
                className="text-sm leading-relaxed text-dim"
              >
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </motion.div>
    </section>
  );
}
